import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TrendingDashboard.css';

const TrendingDashboard = () => {
  const [trades, setTrades] = useState([]);
  const [filteredTrades, setFilteredTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [confidenceFilter, setConfidenceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('confidence_score');
  const [sortDirection, setSortDirection] = useState('desc');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [newsData, setNewsData] = useState({});
  const [selectedNews, setSelectedNews] = useState(null);
  const [loadingNews, setLoadingNews] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');

  // Refresh WSB trades (scan Reddit, add new trades, apply exits)
  const refreshWSBTrades = async () => {
    try {
      setRefreshing(true);
      setRefreshMessage('Scanning r/wallstreetbets...');

      const response = await axios.post('/api/refresh-trades');

      if (response.data.success) {
        setRefreshMessage('Refresh complete! Reloading trades...');
        // Wait a moment then refresh the trades display
        setTimeout(() => {
          fetchTrades(true);
          setRefreshMessage('');
          setRefreshing(false);
        }, 1000);
      } else {
        setRefreshMessage('Refresh failed: ' + response.data.message);
        setRefreshing(false);
      }
    } catch (error) {
      console.error('Error refreshing trades:', error);
      setRefreshMessage('Error: ' + (error.response?.data?.message || error.message));
      setRefreshing(false);
    }
  };

  // Fetch trades data
  const fetchTrades = async (showLoading = false) => {
    try {
      // Only show loading state on initial load or manual refresh, not on auto-refresh
      if (showLoading) {
        setLoading(true);
      }
      // Try multiple endpoints - prioritize WSB portfolio endpoint
      // Use relative URLs so they work from any host/IP
      const endpoints = [
        '/api/wsb-portfolio',
        '/api/trades'
      ];

      let dataFound = false;
      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(endpoint);
          if (response.data) {
            // Ensure response.data is an array
            const tradesData = Array.isArray(response.data) ? response.data : [];
            setTrades(tradesData);
            setFilteredTrades(tradesData);
            setConnectionStatus('connected');
            dataFound = true;

            // Fetch news data for all symbols
            if (tradesData.length > 0) {
              fetchNewsForTrades(tradesData);
            }
            break;
          }
        } catch (err) {
          console.log(`Failed to fetch from ${endpoint}`);
        }
      }

      // If no data found, use mock data
      if (!dataFound) {
        const mockData = generateMockTrades();
        setTrades(mockData);
        setFilteredTrades(mockData);
        setConnectionStatus('offline');
      }
    } catch (error) {
      console.error('Error fetching trades:', error);
      // Use mock data if API fails
      const mockData = generateMockTrades();
      setTrades(mockData);
      setFilteredTrades(mockData);
      setConnectionStatus('offline');
    } finally {
      setLoading(false);
    }
  };

  // Generate mock trades data
  const generateMockTrades = () => {
    const symbols = ['AAPL', 'TSLA', 'NVDA', 'AMD', 'META', 'GOOGL', 'MSFT', 'SPY', 'QQQ', 'GME'];
    const strategies = ['Momentum', 'WSB Mention', 'Options Flow', 'Breakout', 'Volume Spike'];
    const sources = ['Reddit/WSB', 'StockTwits', 'Discord', 'Twitter'];

    return symbols.map((symbol, index) => ({
      id: index + 1,
      symbol,
      strategy: strategies[index % strategies.length],
      post_date: new Date(Date.now() - Math.random() * 86400000).toISOString(),
      entry_price: 100 + Math.random() * 500,
      current_price: 100 + Math.random() * 500,
      percent_gain: -10 + Math.random() * 30,
      confidence_score: 30 + Math.random() * 60,
      source: sources[index % sources.length],
      volume: Math.floor(Math.random() * 10000000),
      mentions: Math.floor(Math.random() * 1000),
      sentiment: Math.random() > 0.5 ? 'bullish' : 'bearish'
    }));
  };

  // Apply filters
  useEffect(() => {
    // Ensure trades is an array before processing
    if (!Array.isArray(trades)) {
      setFilteredTrades([]);
      return;
    }

    let filtered = [...trades];

    // Source filter
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(trade => trade.source === sourceFilter);
    }

    // Confidence filter
    if (confidenceFilter !== 'all') {
      filtered = filtered.filter(trade => {
        if (confidenceFilter === 'HIGH') return trade.confidence_score >= 70;
        if (confidenceFilter === 'MEDIUM') return trade.confidence_score >= 40 && trade.confidence_score < 70;
        if (confidenceFilter === 'LOW') return trade.confidence_score < 40;
        return true;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      const aVal = a[sortBy] || 0;
      const bVal = b[sortBy] || 0;
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    setFilteredTrades(filtered);
  }, [trades, sourceFilter, confidenceFilter, sortBy, sortDirection]);

  // Initialize
  useEffect(() => {
    // Show loading on initial fetch
    fetchTrades(true);
    // Auto-refresh every 60 seconds WITHOUT showing loading state (keeps data visible)
    const interval = setInterval(() => fetchTrades(false), 60000);
    return () => clearInterval(interval);
  }, []);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 70) return 'confidence-high';
    if (score >= 40) return 'confidence-medium';
    return 'confidence-low';
  };

  const getGainColor = (gain) => {
    if (gain > 0) return 'gain-positive';
    if (gain < 0) return 'gain-negative';
    return 'gain-neutral';
  };

  // Format trade date - compact format MM/DD/YYYY
  const formatTradeDate = (dateString) => {
    if (!dateString) return '-';
    // Parse date string directly to avoid timezone issues
    // Handles both "YYYY-MM-DD" and "YYYY-MM-DD HH:MM:SS" formats
    const datePart = dateString.split(' ')[0]; // Get just the date part
    const [year, month, day] = datePart.split('-').map(Number);
    return `${month}/${day}/${year}`;
  };

  // Format time with AM/PM
  const formatTime = (dateString, timeString) => {
    if (!timeString) return '';

    // Parse time (format: HH:MM)
    const [hours, minutes] = timeString.split(':').map(Number);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;

    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  // Format strategy name - split on underscores and caps
  const formatStrategy = (strategy) => {
    if (!strategy) return '-';
    // Replace underscores with spaces
    let formatted = strategy.replace(/_/g, ' ');
    // Split into two lines if it has multiple words
    const words = formatted.split(' ');
    if (words.length > 1) {
      return (
        <>
          {words[0]}<br />{words.slice(1).join(' ')}
        </>
      );
    }
    return formatted;
  };

  // Fetch news status for all trades
  const fetchNewsForTrades = async (tradesData) => {
    try {
      const symbols = [...new Set(tradesData.map(t => t.symbol))]; // Get unique symbols
      const response = await axios.post('/api/news/batch', { symbols });
      setNewsData(response.data || {});
    } catch (error) {
      console.error('Failed to fetch news status:', error);
    }
  };

  // Fetch detailed news for a symbol
  const fetchDetailedNews = async (symbol) => {
    setLoadingNews(true);
    setSelectedNews({ symbol, loading: true });
    try {
      // Use relative URL so it works from any host/IP
      const endpoint = '/api/news/' + symbol;

      const response = await axios.get(endpoint);
      if (response.data) {
        // Check if response has news array directly or nested
        const newsArray = response.data.news || response.data;
        setSelectedNews({
          symbol,
          news: Array.isArray(newsArray) ? newsArray : [],
          loading: false
        });
      } else {
        setSelectedNews({
          symbol,
          news: [],
          error: 'No news available',
          loading: false
        });
      }
    } catch (error) {
      console.error('Failed to fetch news:', error);
      setSelectedNews({
        symbol,
        news: [],
        error: 'Failed to load news',
        loading: false
      });
    } finally {
      setLoadingNews(false);
    }
  };

  // Calculate statistics (ensure filteredTrades is an array)
  const tradesArray = Array.isArray(filteredTrades) ? filteredTrades : [];
  const stats = {
    totalSignals: tradesArray.length,
    totalVolume: tradesArray.reduce((sum, t) => sum + (t.volume || 0), 0),
    avgConfidence: tradesArray.length > 0
      ? (tradesArray.reduce((sum, t) => sum + (t.confidence_score || 0), 0) / tradesArray.length).toFixed(1)
      : 0,
    highConfCount: tradesArray.filter(t => t.confidence_score >= 70).length
  };

  return (
    <div className="trending-dashboard">
      {/* Header */}
      <div className="trending-header">
        <h1 className="trending-title">🔥 Trending Stocks</h1>
        <div className="header-stats">
          <div className="stat">
            <span className="stat-label">Total Signals</span>
            <span className="stat-value">{stats.totalSignals}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Active Volume</span>
            <span className="stat-value">{(stats.totalVolume / 1000000).toFixed(2)}M</span>
          </div>
          <div className="stat">
            <span className="stat-label">Avg Confidence</span>
            <span className="stat-value">{stats.avgConfidence}%</span>
          </div>
          <button
            className={`refresh-button ${refreshing ? 'refreshing' : ''}`}
            onClick={refreshWSBTrades}
            disabled={refreshing}
            style={{
              marginLeft: '20px',
              padding: '10px 20px',
              backgroundColor: refreshing ? '#666' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {refreshing ? '🔄 Refreshing...' : '🔄 Refresh WSB Trades'}
          </button>
        </div>
      </div>
      {refreshMessage && (
        <div style={{
          padding: '10px',
          backgroundColor: '#2196F3',
          color: 'white',
          borderRadius: '5px',
          margin: '10px 0',
          textAlign: 'center'
        }}>
          {refreshMessage}
        </div>
      )}

      {/* Main Content */}
      <div className="trending-content">
        {/* Left Panel - Strength Meters */}
        <div className="trending-sidebar-left">
          <div className="panel strength-panel">
            <div className="panel-header">
              <h3>Strength Meter</h3>
            </div>
            <div className="panel-content">
              {['WSB Momentum', 'Options Flow', 'Volume Spike', 'Social Sentiment', 'Technical Break'].map((metric, i) => {
                const strength = 20 + Math.random() * 60;
                return (
                  <div key={metric} className="strength-item">
                    <span className="strength-label">{metric}</span>
                    <div className="strength-bar-container">
                      <div
                        className={`strength-bar ${strength > 60 ? 'strength-high' : strength > 30 ? 'strength-medium' : 'strength-low'}`}
                        style={{ width: `${strength}%` }}
                      ></div>
                    </div>
                    <span className="strength-value">{strength.toFixed(0)}%</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="panel stats-panel">
            <div className="panel-header">
              <h3>Market Overview</h3>
            </div>
            <div className="panel-content">
              <div className="quick-stats">
                <div className="stat-row">
                  <span>High Confidence</span>
                  <span className="value">{stats.highConfCount}</span>
                </div>
                <div className="stat-row">
                  <span>Bullish Signals</span>
                  <span className="value positive">{tradesArray.filter(t => t.percent_gain > 0).length}</span>
                </div>
                <div className="stat-row">
                  <span>Bearish Signals</span>
                  <span className="value negative">{tradesArray.filter(t => t.percent_gain < 0).length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Center - Trade List */}
        <div className="trending-main">
          <div className="panel trade-panel">
            <div className="panel-header">
              <h3>Top Value List</h3>
              <div className="panel-controls">
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Sources</option>
                  <option value="Reddit/WSB">Reddit/WSB</option>
                  <option value="StockTwits">StockTwits</option>
                  <option value="Discord">Discord</option>
                  <option value="Twitter">Twitter</option>
                </select>
                <select
                  value={confidenceFilter}
                  onChange={(e) => setConfidenceFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Confidence</option>
                  <option value="HIGH">High (70+)</option>
                  <option value="MEDIUM">Medium (40-69)</option>
                  <option value="LOW">Low (&lt;40)</option>
                </select>
                <button className="refresh-btn" onClick={() => fetchTrades(true)}>
                  ↻ Refresh
                </button>
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading trades...</div>
            ) : (
              <div className="trade-table-container">
                <table className="trade-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('status')} className="sortable">
                        STATUS {sortBy === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('symbol')} className="sortable">
                        SYMBOL {sortBy === 'symbol' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('post_date')} className="sortable">
                        POST {sortBy === 'post_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('entry_date')} className="sortable">
                        ENTRY {sortBy === 'entry_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('exit_date')} className="sortable">
                        EXIT {sortBy === 'exit_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('strategy')} className="sortable">
                        STRATEGY {sortBy === 'strategy' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th>STRIKE</th>
                      <th>EXPIRY</th>
                      <th onClick={() => handleSort('entry_price')} className="sortable">
                        ENTRY $ {sortBy === 'entry_price' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('exit_price')} className="sortable">
                        EXIT $ {sortBy === 'exit_price' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('current_price')} className="sortable">
                        CURRENT $ {sortBy === 'current_price' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('percent_gain')} className="sortable">
                        GAIN % {sortBy === 'percent_gain' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('confidence_score')} className="sortable">
                        CONF {sortBy === 'confidence_score' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th>NEWS</th>
                      <th>SOURCE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tradesArray.map(trade => {
                      // Calculate expiration date (3 weeks from entry, next Friday)
                      const getExpiration = () => {
                        if (!trade.entry_date || !trade.strike) return '-';
                        const entry = new Date(trade.entry_date);
                        const exp = new Date(entry);
                        exp.setDate(exp.getDate() + 21);
                        const dayOfWeek = exp.getDay();
                        const daysToFriday = (5 - dayOfWeek + 7) % 7;
                        exp.setDate(exp.getDate() + (daysToFriday === 0 ? 7 : daysToFriday));
                        return exp.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      };

                      return (
                        <tr key={trade.id} className="trade-row">
                          <td onClick={() => setSelectedTrade(trade)}>
                            <span className={`status-badge status-${(trade.status || 'OPEN').toLowerCase()}`}>
                              {trade.status || 'OPEN'}
                            </span>
                          </td>
                          <td className="symbol" onClick={() => setSelectedTrade(trade)}>{trade.symbol}</td>
                          <td onClick={() => setSelectedTrade(trade)} style={{fontSize: '0.85em', minWidth: '100px'}}>
                            {trade.post_date ? (
                              <>
                                {formatTradeDate(trade.post_date)}
                                <div style={{color: '#888', fontSize: '0.9em'}}>
                                  {formatTime(trade.post_date, new Date(trade.post_date).toTimeString().slice(0, 5))}
                                </div>
                              </>
                            ) : '-'}
                          </td>
                          <td onClick={() => setSelectedTrade(trade)} style={{fontSize: '0.85em', minWidth: '100px'}}>
                            {trade.entry_date ? (
                              <>
                                {formatTradeDate(trade.entry_date)}
                                <div style={{color: '#888', fontSize: '0.9em'}}>
                                  {formatTime(trade.entry_date, trade.entry_time || '09:30')}
                                </div>
                              </>
                            ) : '-'}
                          </td>
                          <td onClick={() => setSelectedTrade(trade)} style={{fontSize: '0.85em', minWidth: '100px'}}>
                            {(trade.exit_date && trade.exit_date !== '') ? (
                              <>
                                <div>{formatTradeDate(trade.exit_date)}</div>
                                <div style={{color: '#888', fontSize: '0.9em'}}>
                                  {formatTime(trade.exit_date, trade.exit_time)}
                                </div>
                              </>
                            ) : '-'}
                          </td>
                          <td onClick={() => setSelectedTrade(trade)} style={{fontSize: '0.85em', minWidth: '70px', lineHeight: '1.2'}}>
                            {formatStrategy(trade.strategy)}
                          </td>
                          <td onClick={() => setSelectedTrade(trade)}>
                            {trade.strike ? `$${trade.strike}` : '-'}
                          </td>
                          <td onClick={() => setSelectedTrade(trade)}>
                            {getExpiration()}
                          </td>
                          <td onClick={() => setSelectedTrade(trade)}>${trade.entry_price?.toFixed(2)}</td>
                          <td onClick={() => setSelectedTrade(trade)}>
                            {trade.exit_price ? `$${trade.exit_price.toFixed(2)}` : '-'}
                          </td>
                          <td onClick={() => setSelectedTrade(trade)}>${trade.current_price?.toFixed(2)}</td>
                          <td className={getGainColor(trade.percent_gain)} onClick={() => setSelectedTrade(trade)}>
                            {trade.percent_gain > 0 ? '+' : ''}{trade.percent_gain?.toFixed(2)}%
                          </td>
                          <td className={getConfidenceColor(trade.confidence_score)} onClick={() => setSelectedTrade(trade)}>
                            {trade.confidence_score?.toFixed(0)}%
                          </td>
                          <td
                            className={newsData[trade.symbol]?.has_news ? "news-cell clickable" : "news-cell"}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (newsData[trade.symbol]?.has_news) {
                                fetchDetailedNews(trade.symbol);
                              }
                            }}
                            title={newsData[trade.symbol]?.has_news ? 'Click to view news' : 'No recent news'}
                          >
                            {newsData[trade.symbol]?.has_news ? '📰' : ''}
                          </td>
                          <td
                            className="source clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (trade.url) {
                                window.open(trade.url, '_blank');
                              }
                            }}
                            title={trade.url ? 'Click to view source' : 'No source URL'}
                          >
                            {trade.source} {trade.url ? '🔗' : ''}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Trade Details & News (Slide-out) */}
        <div className={`trending-sidebar-right ${(selectedTrade || selectedNews) ? 'active' : ''}`}>
          <div className="panel details-panel">
            <div className="panel-header">
              <h3>{selectedNews ? `${selectedNews.symbol} News` : 'Trade Details'}</h3>
              <button
                className="close-news"
                onClick={() => {
                  setSelectedNews(null);
                  setSelectedTrade(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="panel-content">
              {selectedNews ? (
                // Show news
                <div className="news-section">
                  {selectedNews.loading ? (
                    <div className="news-loading">
                      <div className="spinner"></div>
                      <p>Loading news...</p>
                    </div>
                  ) : selectedNews.error ? (
                    <div className="news-error">{selectedNews.error}</div>
                  ) : selectedNews.news && selectedNews.news.length > 0 ? (
                    <div className="news-list-sidebar">
                      {selectedNews.news.slice(0, 10).map((article, index) => (
                        <div
                          key={index}
                          className="news-item-sidebar"
                          onClick={() => window.open(article.url, '_blank')}
                        >
                          <div className="news-header">
                            <span className="news-source">{article.source}</span>
                            <span className="news-time">{formatTradeDate(article.datetime)}</span>
                          </div>
                          <h5 className="news-headline">{article.headline}</h5>
                          {article.summary && (
                            <p className="news-summary">{article.summary.substring(0, 150)}...</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-news">No recent news available</div>
                  )}
                </div>
              ) : selectedTrade ? (
                // Show trade details
                <div className="trade-details">
                  <h4>{selectedTrade.symbol}</h4>
                  <div className="detail-row">
                    <span>Strategy:</span>
                    <span>{selectedTrade.strategy}</span>
                  </div>
                  <div className="detail-row">
                    <span>Source:</span>
                    <span
                      className={selectedTrade.url ? "clickable-value" : ""}
                      onClick={() => selectedTrade.url && window.open(selectedTrade.url, '_blank')}
                    >
                      {selectedTrade.source} {selectedTrade.url ? '🔗' : ''}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>Buy Date:</span>
                    <span>{formatTradeDate(selectedTrade.post_date)}</span>
                  </div>
                  <div className="detail-row">
                    <span>Sell Date:</span>
                    <span>{selectedTrade.sell_date ? formatTradeDate(selectedTrade.sell_date) : '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span>Entry Price:</span>
                    <span>${selectedTrade.entry_price?.toFixed(2)}</span>
                  </div>
                  <div className="detail-row">
                    <span>Current Price:</span>
                    <span>${selectedTrade.current_price?.toFixed(2)}</span>
                  </div>
                  <div className="detail-row">
                    <span>Gain:</span>
                    <span className={getGainColor(selectedTrade.percent_gain)}>
                      {selectedTrade.percent_gain?.toFixed(2)}%
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>Confidence:</span>
                    <span className={getConfidenceColor(selectedTrade.confidence_score)}>
                      {selectedTrade.confidence_score?.toFixed(0)}%
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>Volume:</span>
                    <span>{(selectedTrade.volume / 1000000).toFixed(2)}M</span>
                  </div>
                  <div className="detail-row">
                    <span>Mentions:</span>
                    <span>{selectedTrade.mentions}</span>
                  </div>
                  <div className="detail-row">
                    <span>Sentiment:</span>
                    <span className={`sentiment-${selectedTrade.sentiment}`}>
                      {selectedTrade.sentiment}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>News:</span>
                    <span
                      className={newsData[selectedTrade.symbol]?.has_news ? "clickable-value news-available" : ""}
                      onClick={() => newsData[selectedTrade.symbol]?.has_news && fetchDetailedNews(selectedTrade.symbol)}
                    >
                      {newsData[selectedTrade.symbol]?.has_news ? '📰 View News' : 'No news'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="no-selection">Select a trade to view details</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="status-bar">
        <div className="status-left">
          <div className="connection-status">
            <span className={`status-dot ${connectionStatus}`}></span>
            <span>{connectionStatus === 'connected' ? 'Connected' : 'Offline Mode'}</span>
          </div>
        </div>
        <div className="status-center">
          <span>{tradesArray.length} trades</span>
          {Object.values(newsData).filter(n => n.has_news).length > 0 && (
            <span className="news-count">
              | {Object.values(newsData).filter(n => n.has_news).length} with news
            </span>
          )}
        </div>
        <div className="status-right">
          <span>Last update: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>
    </div>
  );
};

export default TrendingDashboard;